// The care team's review of a week-one content plan: confirm, edit, redo parts, preview the draft, approve.
//
// The server holds the plan; this page keeps a working copy plus the reviewer's overlays (removed items, medication
// statuses, dose choices) and saves the combined plan (logic.finalPlan) a moment after each change. Steps that call
// the model run as a job on the report; the page polls while one runs and reloads the plan when it finishes.
// Confirmation ticks are kept in this browser only (localStorage), per report.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, mockOn, openPdf } from "./api";
import { LookupContext } from "./parts";
import * as S from "./Sections";
import { blockers, clearConfirmations, elapsed, finalPlan, fmtDate, initialDoseChoices, setPath, statusChanges } from "./logic";
import "./WeekOneReview.css";

// How often the page checks a running step, and how long its checks can fail before it says it lost track: a deploy
// restarts the service for a few seconds, which shouldn't raise a warning. Exported so tests can shorten them.
export const timing = { pollMs: 1500, lostAfterMs: 30000 };
const SAVE_MS = 800;
const STEP_LABEL = { plan: "Writing the content plan", revise: "Updating the plan", preview: "Writing a draft to preview", write: "Writing the final report" };

const emptyUi = () => ({ confirmed: {}, edited: {}, removed: {}, medStatus: {}, doseChoice: {}, note: "", name: "", purpose: "" });

function loadConfirmed(id) {
  try {
    return JSON.parse(localStorage.getItem(`weekOne:${id}:confirmed`) || "{}");
  } catch {
    return {};
  }
}

function storeConfirmed(id, confirmed) {
  try {
    localStorage.setItem(`weekOne:${id}:confirmed`, JSON.stringify(confirmed));
  } catch {
    /* private window or storage blocked: ticks just won't survive a reload */
  }
}

export default function WeekOneReview() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [view, setView] = useState(null);
  const [plan, setPlan] = useState(null);
  const [original, setOriginal] = useState([]); // differential as loaded, to tell which statuses the reviewer changed
  const [ui, setUi] = useState(emptyUi);
  const [error, setError] = useState(null);
  const [save, setSave] = useState({ dirty: false, state: "saved" });
  const [now, setNow] = useState(Date.now());
  const latest = useRef({});
  latest.current = { plan, ui, view };
  const saveTimer = useRef(null);
  const seenRevisions = useRef(null);

  // Take the server's plan as the working copy, dropping overlays it now includes.
  const adopt = useCallback((v) => {
    setPlan(v.plan ? JSON.parse(JSON.stringify(v.plan)) : null);
    setOriginal(v.plan ? v.plan.differential.map((d) => ({ name: d.name, status: d.status })) : []);
    const revs = (v.revisions || []).length;
    const last = (v.revisions || [])[revs - 1];
    const seen = seenRevisions.current; // read now: the updater below runs after it's reset
    setUi((u) => {
      let confirmed = seen === null ? loadConfirmed(v.id) : u.confirmed;
      if (seen !== null && revs > seen && last) confirmed = clearConfirmations(confirmed, last.changed);
      storeConfirmed(v.id, confirmed);
      return { ...u, confirmed, edited: {}, removed: {}, medStatus: {}, doseChoice: initialDoseChoices(v.review && v.review.dose_conflicts) };
    });
    seenRevisions.current = revs;
    setSave({ dirty: false, state: "saved" });
  }, []);

  const load = useCallback(async ({ adoptPlan }) => {
    try {
      const v = await api().getReport(reportId);
      setView(v);
      if (adoptPlan) adopt(v);
      return v;
    } catch (e) {
      setError(e.message);
      return null;
    }
  }, [reportId, adopt]);

  useEffect(() => {
    seenRevisions.current = null;
    setUi(emptyUi());
    load({ adoptPlan: true });
  }, [load]);

  // Poll while a job runs; when it finishes, reload (and take the new plan if the job changed it).
  const jobState = view && view.job && view.job.state;
  const jobKind = view && view.job && view.job.kind;
  // A failed check is tried again; once checks have failed for timing.lostAfterMs the page stops showing the step as
  // running and says so, rather than spinning on a status it can no longer read.
  const [lost, setLost] = useState(null);
  const [missSince, setMissSince] = useState(null); // when the current run of failed checks began
  const [misses, setMisses] = useState(0);
  useEffect(() => {
    if (jobState !== "running" || lost) return undefined;
    const t = setTimeout(async () => {
      let v;
      try {
        v = await api().getReport(reportId);
      } catch (e) {
        const since = missSince || Date.now();
        if (Date.now() - since >= timing.lostAfterMs) setLost(e.message);
        setMissSince(since);
        setMisses(misses + 1);
        return;
      }
      setMissSince(null);
      setMisses(0);
      const finished = !(v.job && v.job.state === "running");
      setView(v);
      if (finished && ["plan", "revise"].includes(jobKind) && v.job.state === "done") adopt(v);
    }, timing.pollMs);
    return () => clearTimeout(t);
  }, [view, jobState, jobKind, reportId, adopt, lost, missSince, misses]);

  const checkAgain = () => { // polling picks up again; it takes the finished step's result as usual
    setLost(null);
    setMissSince(null);
    setMisses(0);
  };

  // What the reviewer is shown also goes to the service's logs, so a problem is seen without waiting to be told.
  useEffect(() => {
    if (error) api().logError({ reportId, where: "review", message: error });
  }, [error, reportId]);
  useEffect(() => {
    if (lost) api().logError({ reportId, where: "status checks", message: lost });
  }, [lost, reportId]);

  useEffect(() => {
    if (jobState !== "running" || lost) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [jobState, lost]);

  // ── saving ──
  const saveNow = useCallback(async () => {
    clearTimeout(saveTimer.current);
    const { plan: p, ui: u, view: v } = latest.current;
    if (!p || !v) return true;
    setSave({ dirty: false, state: "saving" });
    try {
      await api().savePlan(reportId, finalPlan(p, u, v.review));
      setSave((s) => ({ ...s, state: s.dirty ? "pending" : "saved" }));
      if (v.previewDraft) load({ adoptPlan: false }); // whether the preview still matches the plan
      return true;
    } catch (e) {
      setSave({ dirty: true, state: "error" });
      setError(`Couldn't save: ${e.message}`);
      return false;
    }
  }, [reportId, load]);

  const changed = useCallback(() => {
    setSave({ dirty: true, state: "pending" });
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(saveNow, SAVE_MS);
  }, [saveNow]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  // Every step that runs on the server works from the saved plan, so save first.
  const startJob = useCallback(async (fn) => {
    setError(null);
    if (latest.current.ui && save.dirty && !(await saveNow())) return false;
    try {
      await fn();
      await load({ adoptPlan: false });
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    }
  }, [save.dirty, saveNow, load]);

  // ── what the sections call ──
  const c = view && plan && view.review ? {
    plan, ui, review: view.review, stage: view.review.member.stage,
    statusChanges: statusChanges(plan, original),
    updated: new Set(((view.revisions || []).slice(-1)[0] || {}).changed || []),
    confirm: (key, v) => setUi((u) => {
      const confirmed = { ...u.confirmed, [key]: v };
      storeConfirmed(reportId, confirmed);
      return { ...u, confirmed };
    }),
    saveField: (path, value) => {
      setPlan((p) => setPath(p, path, value));
      setUi((u) => ({ ...u, edited: { ...u.edited, [path]: true } }));
      changed();
    },
    saveLever: async (text) => {
      const was = plan.lever.action;
      latest.current.plan = setPath(plan, "lever.action", text);
      setPlan(latest.current.plan);
      setSave((s) => ({ ...s, dirty: true }));
      await startJob(async () => {
        await saveNow();
        await api().revise(reportId, { lever: text, before: { lever: was } });
      });
    },
    toggleRemove: (key) => {
      setUi((u) => {
        const removed = { ...u.removed, [key]: !u.removed[key] };
        const confirmed = { ...u.confirmed };
        if (removed[key]) delete confirmed[key];
        return { ...u, removed, confirmed };
      });
      changed();
    },
    setStatus: (i, v) => {
      setPlan((p) => setPath(p, `differential.${i}.status`, v));
      changed();
    },
    updateForStatuses: () => {
      const statuses = statusChanges(plan, original);
      const was = Object.fromEntries(original.filter((d) => d.name in statuses).map((d) => [d.name, d.status]));
      return startJob(() => api().revise(reportId, { statuses, before: { statuses: was } }));
    },
    setMed: (id, v) => {
      const g = (plan.medications || []).find((m) => m.id === id);
      setUi((u) => {
        const medStatus = { ...u.medStatus };
        if (g && g.status === v) delete medStatus[id];
        else medStatus[id] = v;
        return { ...u, medStatus };
      });
      changed();
    },
    pickDose: (id, pick) => {
      setUi((u) => ({ ...u, doseChoice: { ...u.doseChoice, [id]: { ...(u.doseChoice[id] || {}), pick } } }));
      changed();
    },
    setDoseOther: (id, other) => {
      setUi((u) => ({ ...u, doseChoice: { ...u.doseChoice, [id]: { ...(u.doseChoice[id] || {}), other } } }));
      changed();
    },
    setPlaceholder: (i, value) => {
      setPlan((p) => setPath(p, `placeholders.${i}.value`, value));
      changed();
    },
    setNote: (note) => setUi((u) => ({ ...u, note })),
    reviseWithNote: async () => {
      if (await startJob(() => api().revise(reportId, { note: ui.note.trim() }))) setUi((u) => ({ ...u, note: "" }));
    },
  } : null;

  const undo = () => startJob(async () => {
    await api().undo(reportId);
    const v = await api().getReport(reportId);
    setView(v);
    adopt(v);
  });
  const retry = () => startJob(async () => {
    const id = await api().retry(reportId);
    if (id && id !== reportId) navigate(`/first-report/${id}`);
  });
  const preview = (note = "") => startJob(() => api().preview(reportId, note));
  const approve = () => startJob(() => api().approve(reportId, { approverName: ui.name.trim(), purpose: ui.purpose, plan: finalPlan(plan, ui, view.review) }));
  const pdf = (which) => openPdf(reportId, which).catch((e) => setError(e.message));

  // ── render ──
  if (!view)
    return (
      <div className="w1"><div className="wrap">
        {error
          ? <div className="err" role="alert"><span>{error}</span><button onClick={() => window.location.reload()}>Reload</button></div>
          : <p className="muted">Loading the report…</p>}
      </div></div>
    );

  const job = view.job;
  const running = job && job.state === "running";
  const draft = view.status === "draft";
  const name = (view.review && view.review.member.name) || view.memberName || "member";
  const st = view.review && view.review.member.stage;
  const last = (view.revisions || []).slice(-1)[0];
  const { req, done, b } = c ? blockers(plan, ui, view.review) : { req: [], done: 0, b: [] };

  return (
    <LookupContext.Provider value={(view.review && view.review.lookup) || {}}>
      <div className={`w1 ${running ? "busy" : ""}`}>
        <div className="wrap">
          <Link className="back" to={`/user/${view.memberId}/reports?type=first`}>← {name}'s reports</Link>
          <header className="top">
            <div>
              <h1>Report review · {name} {mockOn() && <span className="mock">Mock data</span>}</h1>
              {st ? (
                <div className="sub">
                  Data {fmtDate(st.window_start)} – {fmtDate(st.window_end)} · report date {fmtDate(st.report_date)} · seven-day check reads on {fmtDate(st.check_date)}
                  {plan && plan.pages > 2 && plan.pages_reason && <> · longer than usual: {plan.pages_reason.replace(/\.$/, "")}</>}
                </div>
              ) : <div className="sub">Report date {fmtDate(view.reportDate)}</div>}
            </div>
          </header>

          {error && <div className="err" role="alert"><span>{error}</span><button onClick={() => setError(null)}>Dismiss</button></div>}
          <JobBanner job={job} now={now} onRetry={retry} lost={lost} onCheckAgain={checkAgain} />

          {view.status !== "draft" && <ApprovedPanel view={view} onPdf={pdf} />}

          {c && draft && (
            <>
              {last && (
                <div className="banner" style={{ background: "var(--blue-soft)", borderColor: "#BFD3F7" }}>
                  <b>Updated from your {last.kind === "statuses" ? "status change" : last.kind === "lever" ? "action" : "note"}</b> ({Math.round(last.seconds)}s): “{last.note}”<br />
                  Changed: <b>{last.changed.map((x) => x.replace(/_/g, " ")).join(", ") || "nothing"}</b>. Changed parts are outlined in blue and need confirming again. Everything else is exactly as before.{" "}
                  <button className="rm" onClick={undo}>Undo (back to the previous version)</button>
                </div>
              )}
              <div className="how">
                <span>Check the clinical and safety content. Confirm the items marked in red, edit anything that's wrong, and remove what shouldn't be in the report. You don't need to check tone or wording; the writer and automated checks handle that.</span>
                <span className="progress">{done} of {req.length} confirmed<span className="bar"><i style={{ width: `${req.length ? (100 * done) / req.length : 100}%` }} /></span></span>
              </div>
              <S.Banners review={view.review} />
              <S.Headline c={c} />
              <S.Finding c={c} />
              <S.Differential c={c} />
              <S.Lever c={c} />
              <S.Check c={c} />
              <S.DoctorAsk c={c} />
              <S.Medications c={c} />
              <S.Safety c={c} />
              <S.LongerGame c={c} />
              <S.People c={c} />
              <S.Placeholders c={c} />
              <S.PageOne c={c} />
              <S.Evidence c={c} />
              <S.ReviewerNotes c={c} />
              <S.SomethingOff c={c} />
              <PreviewPanel view={view} onPreview={preview} onPdf={pdf} />
            </>
          )}
        </div>

        {c && draft && (
          <footer className="dock">
            <div className="in">
              <div className="approve">
                <input type="text" placeholder="Your name" value={ui.name} onChange={(e) => setUi((u) => ({ ...u, name: e.target.value }))} style={{ width: 170 }} aria-label="Your name" />
                <div className="radio">
                  <label><input type="radio" name="purpose" checked={ui.purpose === "member"} onChange={() => setUi((u) => ({ ...u, purpose: "member" }))} />Going to the member</label>
                  <label title="Testing, an example to show someone, call analysis"><input type="radio" name="purpose" checked={ui.purpose === "other"} onChange={() => setUi((u) => ({ ...u, purpose: "other" }))} />Other (testing, example, call analysis)</label>
                </div>
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <span className="muted" style={{ fontSize: 12 }}>{{ saving: "Saving…", pending: "Saving…", error: "Not saved", saved: "Saved" }[save.state]}</span>
                <button className="ghost" onClick={() => preview()}>Preview draft</button>
                <button className="primary" disabled={b.length > 0} onClick={approve}>Approve and write the report</button>
              </div>
              {b.length > 0 && <div className="blockers">Still needed: {b.join(", ")}</div>}
            </div>
          </footer>
        )}
      </div>
    </LookupContext.Provider>
  );
}

function JobBanner({ job, now, onRetry, lost, onCheckAgain }) {
  if (!job) return null;
  if (job.state === "running" && lost)
    return (
      <div className="job failed" role="alert">
        <span className="grow">
          <b>{STEP_LABEL[job.kind] || "The last step"} may still be going, but this page hasn't been able to check on it for a while.</b> {lost}
        </span>
        <button className="ghost small" onClick={onCheckAgain}>Check again</button>
      </div>
    );
  if (job.state === "running")
    return (
      <div className="job" role="status" style={{ position: "sticky", top: 0, zIndex: 5 }}>
        <span className="spin" />
        <span className="grow"><b>{STEP_LABEL[job.kind] || "Working"}</b>: {job.step}… <span className="muted">{elapsed(job.startedAt, now)}</span></span>
        <span className="muted" style={{ fontSize: 12 }}>You can leave this page; it keeps going.</span>
      </div>
    );
  if (job.state === "failed" || job.state === "stale")
    return (
      <div className="job failed" role="alert">
        <span className="grow">
          <b>{STEP_LABEL[job.kind] || "The last step"} {job.state === "stale" ? "was interrupted" : "didn't finish"}.</b>{" "}
          {job.state === "stale" ? "The service restarted while it ran." : job.error}
        </span>
        <button className="ghost small" onClick={onRetry}>Try again</button>
      </div>
    );
  return null;
}

function PreviewPanel({ view, onPreview, onPdf }) {
  const pd = view.previewDraft;
  const notes = view.writerNotes || [];
  const [note, setNote] = useState("");
  const send = async () => {
    if (await onPreview(note.trim())) setNote("");
  };
  return (
    <>
      <section className="panel">
        <h2>Preview {pd && pd.outOfDate && <span className="tag req">Out of date: the plan changed since</span>}</h2>
        <p className="why" style={{ margin: "2px 0 10px", color: "var(--slate)", fontSize: 12.5 }}>
          Optional. Writes the report from the plan as it is now, so you can read it before approving. If the plan hasn't changed
          since, approving sends this preview exactly as it is.
        </p>
        {pd ? (
          <>
            <div className="row">
              <span>Draft from {new Date(pd.at).toLocaleString("en-US")} · {pd.pages} page{pd.pages === 1 ? "" : "s"}</span>
              <button className="ghost small" onClick={() => onPdf("preview")}>Open the draft PDF</button>
              {pd.outOfDate && <button className="ghost small" onClick={() => onPreview()}>Preview again</button>}
            </div>
            {(pd.leftovers || []).length > 0 && (
              <div className="banner" style={{ margin: "10px 0 0" }}>
                <b>What the writer couldn't fix.</b> These are wording problems; we're tracking them, and they don't need you unless one changes the meaning.
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {pd.leftovers.map((x, i) => <li key={i}><span className="muted">{(x.slot || "").replace(/_/g, " ")}:</span> {x.problem}</li>)}
                </ul>
              </div>
            )}
            {(pd.cuts || []).length > 0 && <p className="muted" style={{ margin: "8px 0 0" }}>Cut to fit the pages: {pd.cuts.join("; ")}</p>}
          </>
        ) : (
          <button className="ghost" onClick={() => onPreview()}>Preview draft</button>
        )}
      </section>
      {pd && (
        <section className="card" data-section="preview_feedback">
          <h2>Preview feedback</h2>
          <p className="why">
            On how the preview reads: tone, length, a word to avoid. The writer changes only what your note is about and the plan
            stays as it is. Your notes are kept and followed every time this report is written. To change what the report says, use
            Plan feedback above.
          </p>
          {notes.length > 0 && (
            <ul className="muted" style={{ margin: "0 0 8px", paddingLeft: 18, fontSize: 12.5 }}>
              {notes.map((n, i) => <li key={i}>“{n.note}”</li>)}
            </ul>
          )}
          <textarea placeholder="e.g. Too long on page 2, and don't call her eating restrictive." value={note} onChange={(e) => setNote(e.target.value)} aria-label="Preview feedback" />
          <div style={{ marginTop: 8 }}>
            <button className="ghost" disabled={!note.trim()} onClick={send}>Rewrite the preview with this note</button>
          </div>
        </section>
      )}
    </>
  );
}

function ApprovedPanel({ view, onPdf }) {
  const ap = view.approval || {};
  const f = view.final;
  return (
    <section className="panel">
      <h2>{view.status === "ready" ? "Report written" : "Approved"} <span className="tag ok">{ap.purpose === "member" ? "Going to the member" : "Other (doesn't count as sent)"}</span></h2>
      <p style={{ margin: "4px 0 10px" }} className="muted">Approved by {ap.name}{ap.at ? ` · ${new Date(ap.at).toLocaleString("en-US")}` : ""}</p>
      {f ? (
        <div className="row">
          <span>{f.pages} page{f.pages === 1 ? "" : "s"}{f.fromPreview ? " · the preview you approved" : ""}</span>
          <button className="primary small" onClick={() => onPdf("final")}>Open the final PDF</button>
          {f.driveFileId && <a href={`https://drive.google.com/file/d/${f.driveFileId}/view`} target="_blank" rel="noreferrer">Open in Drive</a>}
          {(f.checks_final || []).length > 0 && <span className="muted">{f.checks_final.length} wording check{f.checks_final.length === 1 ? "" : "s"} left unfixed</span>}
        </div>
      ) : (
        <p className="muted" style={{ margin: 0 }}>The report is being written from the approved plan.</p>
      )}
    </section>
  );
}
